/**
 * Fork of the legacy auction ZipCodeMap for US ZCTA *collections* (no auction gating).
 * Centroids are filtered to official Census ZCTA5 codes. Centers on browser geolocation when allowed;
 * search recenters; large tap targets on markers.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import { getMapTilerStreetTileUrl } from "../../utils/mapTilerStreetTileUrl";
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Fade,
  TextField,
  Button,
  Stack,
  Alert,
} from "@mui/material";
import { TouchApp, Search as SearchIcon, MyLocation as MyLocationIcon } from "@mui/icons-material";
import { loadNearbyZipCodes } from "../dashboard/auction/data/zipCodes";
import ZipCollectionMarker from "./ZipCollectionMarker";
import ZctaCollectionDetailModal from "./ZctaCollectionDetailModal";
import "leaflet/dist/leaflet.css";
import { useFacility } from "@healthdesk/shared-hooks";
import { geocodeUsSearch } from "../../services/mapCarousel/geocodeUsSearch";

const US_FALLBACK_CENTER = { lat: 39.8283, lng: -98.5795 };

const MAP_ZOOM_DEFAULT = 11;
const MAP_ZOOM_AFTER_SEARCH = 12;

function MapFlyTo({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center?.lat == null || center?.lng == null) return;
    const z = typeof zoom === "number" ? zoom : map.getZoom();
    map.flyTo([center.lat, center.lng], z, { duration: 0.7 });
  }, [center.lat, center.lng, zoom, map]);
  return null;
}

const ZipCollectionMapPicker = ({
  selectedZips,
  onZipToggle,
  fallbackMapCenter = US_FALLBACK_CENTER,
  height = 380,
  /**
   * `modal` — tap marker opens Census detail dialog; user adds via "Add to list".
   * `direct` — tap toggles the list immediately (use with lasso / bulk selection; skips modal).
   */
  markerSelectionMode = "modal",
}) => {
  const { data: facility } = useFacility();
  const defaultCenter = useMemo(
    () => ({
      lat: facility?.lat ?? fallbackMapCenter.lat,
      lng: facility?.lng ?? fallbackMapCenter.lng,
    }),
    [facility?.lat, facility?.lng, fallbackMapCenter.lat, fallbackMapCenter.lng]
  );

  const defaultCenterRef = useRef(defaultCenter);
  defaultCenterRef.current = defaultCenter;

  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [geoResolved, setGeoResolved] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [flyZoom, setFlyZoom] = useState(MAP_ZOOM_DEFAULT);
  const [detailZip, setDetailZip] = useState(null);

  useEffect(() => {
    setMapCenter((prev) => {
      if (geoResolved) return prev;
      return defaultCenter;
    });
  }, [defaultCenter.lat, defaultCenter.lng, geoResolved]);

  useEffect(() => {
    let cancelled = false;
    let settled = false;

    const applyGeolocationFallback = () => {
      if (settled || cancelled) return;
      settled = true;
      setGeoResolved(true);
      const d = defaultCenterRef.current;
      setMapCenter({ lat: d.lat, lng: d.lng });
    };

    if (typeof navigator === "undefined" || !navigator.geolocation) {
      applyGeolocationFallback();
      return () => {
        cancelled = true;
      };
    }

    const timeoutMs = 11000;
    const timer = setTimeout(applyGeolocationFallback, timeoutMs);

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (cancelled || settled) return;
        settled = true;
        clearTimeout(timer);
        setGeoResolved(true);
        setMapCenter({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
        setFlyZoom(MAP_ZOOM_DEFAULT);
      },
      () => {
        clearTimeout(timer);
        applyGeolocationFallback();
      },
      {
        enableHighAccuracy: true,
        timeout: timeoutMs - 500,
        maximumAge: 120000,
      }
    );

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, []);

  const centerLat = mapCenter.lat;
  const centerLng = mapCenter.lng;

  const [loading, setLoading] = useState(true);
  const [visibleMarkers, setVisibleMarkers] = useState([]);
  const FIXED_RADIUS_KM = 50;
  const MAX_MARKERS = 150;

  useEffect(() => {
    if (centerLat == null || centerLng == null) return;

    const loadZipCodes = async () => {
      setLoading(true);
      try {
        const zipCodes = await loadNearbyZipCodes(centerLat, centerLng, FIXED_RADIUS_KM);
        const selectedZipArray = Array.from(selectedZips);
        const priorityZips = selectedZipArray
          .filter((zipId) => zipCodes[zipId])
          .map((zipId) => zipCodes[zipId]);

        const otherZips = Object.values(zipCodes)
          .filter((zip) => !selectedZipArray.includes(String(zip.id)))
          .filter((zip) => zip.lat && zip.lng)
          .sort((a, b) => {
            const distA = Math.sqrt(
              Math.pow(a.lat - centerLat, 2) + Math.pow(a.lng - centerLng, 2)
            );
            const distB = Math.sqrt(
              Math.pow(b.lat - centerLat, 2) + Math.pow(b.lng - centerLng, 2)
            );
            return distA - distB;
          })
          .slice(0, MAX_MARKERS - priorityZips.length);

        setVisibleMarkers([...priorityZips, ...otherZips]);
      } catch (error) {
        console.error("ZipCollectionMapPicker: load failed", error);
      } finally {
        setLoading(false);
      }
    };

    loadZipCodes();
  }, [centerLat, centerLng, selectedZips]);

  const handleZipToggle = useCallback(
    (zipCode) => {
      if (onZipToggle) onZipToggle(zipCode);
    },
    [onZipToggle]
  );

  const handleOpenDetail = useCallback((zip) => {
    setDetailZip(zip);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setDetailZip(null);
  }, []);

  const runSearch = useCallback(async () => {
    const q = searchQuery.trim();
    if (!q) {
      setSearchError("Enter a hospital name, business, or street address.");
      return;
    }
    setSearching(true);
    setSearchError(null);
    try {
      const { lat, lng } = await geocodeUsSearch(q);
      setMapCenter({ lat, lng });
      setFlyZoom(MAP_ZOOM_AFTER_SEARCH);
    } catch (e) {
      setSearchError(e?.message || "Search failed.");
    } finally {
      setSearching(false);
    }
  }, [searchQuery]);

  const recenterOnMe = useCallback(() => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setSearchError("Location is not available in this browser.");
      return;
    }
    setSearchError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setMapCenter({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
        setFlyZoom(MAP_ZOOM_DEFAULT);
      },
      () => setSearchError("Could not read your location. Check browser permissions."),
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
    );
  }, []);

  if (centerLat == null || centerLng == null) {
    return (
      <Box
        sx={{
          mb: 2,
          height,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress size={28} />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Preparing map…
        </Typography>
      </Box>
    );
  }

  const showHint = !loading && visibleMarkers.length === 0;

  return (
    <Box sx={{ mb: 1 }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 1.5 }} alignItems={{ sm: "flex-start" }}>
        <TextField
          fullWidth
          size="small"
          label="Find location"
          placeholder="Hospital, clinic, or address"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          disabled={searching}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              runSearch();
            }
          }}
        />
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          <Button
            variant="contained"
            startIcon={searching ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
            onClick={runSearch}
            disabled={searching}
            sx={{ whiteSpace: "nowrap" }}
          >
            Search
          </Button>
          <Button
            variant="outlined"
            startIcon={<MyLocationIcon />}
            onClick={recenterOnMe}
            aria-label="Recenter map on my location"
            sx={{ whiteSpace: "nowrap" }}
          >
            My location
          </Button>
        </Stack>
      </Stack>
      {searchError ? (
        <Alert severity="warning" sx={{ mb: 1.5 }} onClose={() => setSearchError(null)}>
          {searchError}
        </Alert>
      ) : null}

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        The map loads <strong>ZCTA / Census area</strong> markers (official ZCTA5 list) within about{" "}
        {FIXED_RADIUS_KM} km of the center (your location when allowed, otherwise your facility or a US
        default). Use search or &quot;My location&quot; to move the center.
        {markerSelectionMode === "modal" ? (
          <>
            {" "}
            <strong>Tap a marker</strong> to open Census estimates and use <strong>Add to list</strong> (two
            steps). Codes already in your list but outside this radius stay in the list without a marker here.
          </>
        ) : (
          <>
            {" "}
            <strong>Direct mode:</strong> tap adds or removes a code immediately (for bulk / lasso tools).
            The detail modal is skipped.
          </>
        )}
      </Typography>

      <Box sx={{ height, width: "100%", position: "relative" }}>
        {loading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              backgroundColor: "rgba(255,255,255,0.7)",
            }}
          >
            <CircularProgress />
          </Box>
        )}
        <MapContainer
          center={[centerLat, centerLng]}
          zoom={MAP_ZOOM_DEFAULT}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom
        >
          <MapFlyTo center={mapCenter} zoom={flyZoom} />
          <TileLayer
            url={getMapTilerStreetTileUrl()}
            attribution='&copy; <a href="https://www.maptiler.com/copyright/">MapTiler</a>'
          />
          {visibleMarkers.map((zip) => (
            <ZipCollectionMarker
              key={zip.id}
              zip={zip}
              isSelected={selectedZips.has(String(zip.id))}
              onToggle={handleZipToggle}
              onOpenDetail={handleOpenDetail}
              markerSelectionMode={markerSelectionMode}
            />
          ))}
          <Fade in={showHint} timeout={500}>
            <Box
              sx={{
                position: "absolute",
                bottom: 16,
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 500,
                pointerEvents: "none",
              }}
            >
              <Paper
                elevation={2}
                sx={{
                  px: 2,
                  py: 1.5,
                  textAlign: "center",
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                  backdropFilter: "blur(8px)",
                  borderRadius: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <TouchApp sx={{ fontSize: 20, color: "primary.main", opacity: 0.8 }} />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    No ZCTA markers in range, or still loading.
                  </Typography>
                </Box>
              </Paper>
            </Box>
          </Fade>
        </MapContainer>
      </Box>

      <ZctaCollectionDetailModal
        open={Boolean(detailZip)}
        zip={detailZip}
        isSelected={detailZip ? selectedZips.has(String(detailZip.id)) : false}
        onClose={handleCloseDetail}
        onAddOrRemove={handleZipToggle}
      />
    </Box>
  );
};

export default ZipCollectionMapPicker;
